// src/features/dashboard/components/EmployeeActiveHoursReport/WeeklyActiveHoursDetail.tsx

import {
  WeeklyActiveHoursDetailResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

interface Props {
  data: WeeklyActiveHoursDetailResponse[];
}

/* ============================================================================
   TIME → MINUTES

   Supports:
   - HH:mm:ss
   - H:mm:ss
   - "11h 40m"
   - "10h 22m"
   - "40m"
   - "2h"
   - "0m"
   ============================================================================ */

const timeToMinutes = (
  value?: string | null,
): number => {
  if (!value) {
    return 0;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return 0;
  }

  /* --------------------------------------------------------------------------
     FORMAT 1: HH:mm:ss
     Example:
     07:12:36
     11:40:00
     -------------------------------------------------------------------------- */

  if (trimmed.includes(':')) {
    const parts = trimmed
      .split(':')
      .map(Number);

    if (parts.length === 3) {
      const hours = Number.isFinite(parts[0])
        ? parts[0]
        : 0;

      const minutes = Number.isFinite(parts[1])
        ? parts[1]
        : 0;

      const seconds = Number.isFinite(parts[2])
        ? parts[2]
        : 0;

      return (
        hours * 60 +
        minutes +
        seconds / 60
      );
    }
  }

  /* --------------------------------------------------------------------------
     FORMAT 2: "11h 40m"
     FORMAT 3: "11h"
     FORMAT 4: "40m"

     Examples:
     11h 40m
     10h 22m
     40m
     2h
     -------------------------------------------------------------------------- */

  const hoursMatch = trimmed.match(
    /(\d+(?:\.\d+)?)\s*h/i,
  );

  const minutesMatch = trimmed.match(
    /(\d+(?:\.\d+)?)\s*m/i,
  );

  const hours = hoursMatch
    ? Number(hoursMatch[1])
    : 0;

  const minutes = minutesMatch
    ? Number(minutesMatch[1])
    : 0;

  if (
    Number.isFinite(hours) ||
    Number.isFinite(minutes)
  ) {
    return (
      (Number.isFinite(hours) ? hours : 0) * 60 +
      (Number.isFinite(minutes) ? minutes : 0)
    );
  }

  return 0;
};

/* ============================================================================
   MINUTES → HH:mm:ss
   ============================================================================ */

const minutesToTime = (
  totalMinutes: number,
): string => {
  if (!Number.isFinite(totalMinutes)) {
    return '—';
  }

  const roundedMinutes =
    Math.round(totalMinutes);

  const hours =
    Math.floor(
      roundedMinutes / 60,
    );

  const minutes =
    roundedMinutes % 60;

  return `${hours
    .toString()
    .padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:00`;
};

/* ============================================================================
   TIME DISPLAY
   ============================================================================ */

const formatTime = (
  value?: string | null,
): string => {
  if (!value) {
    return '—';
  }

  return value;
};

/* ============================================================================
   PERCENTAGE → NUMBER
   ============================================================================ */

const percentageToNumber = (
  value?: string | null,
): number => {
  if (!value) {
    return 0;
  }

  const numericValue = Number(
    value
      .replace('%', '')
      .trim(),
  );

  return Number.isFinite(
    numericValue,
  )
    ? numericValue
    : 0;
};

/* ============================================================================
   VS TEAM → MINUTES

   Supports:
   - +HH:mm:ss
   - -HH:mm:ss
   - +0m
   - -10m
   - +1h 20m
   - -1h 20m
   ============================================================================ */

const vsTeamToMinutes = (
  value?: string | null,
): number => {
  if (!value) {
    return 0;
  }

  const trimmed =
    value.trim();

  if (!trimmed) {
    return 0;
  }

  let sign = 1;

  let cleanValue =
    trimmed;

  /* --------------------------------------------------------------------------
     SIGN
     -------------------------------------------------------------------------- */

  if (cleanValue.startsWith('+')) {
    cleanValue =
      cleanValue.substring(1);
  } else if (
    cleanValue.startsWith('-')
  ) {
    sign = -1;

    cleanValue =
      cleanValue.substring(1);
  }

  cleanValue =
    cleanValue.trim();

  /* --------------------------------------------------------------------------
     FORMAT 1: HH:mm:ss
     -------------------------------------------------------------------------- */

  if (cleanValue.includes(':')) {
    const parts = cleanValue
      .split(':')
      .map(Number);

    if (parts.length === 3) {
      const hours =
        Number.isFinite(parts[0])
          ? parts[0]
          : 0;

      const minutes =
        Number.isFinite(parts[1])
          ? parts[1]
          : 0;

      const seconds =
        Number.isFinite(parts[2])
          ? parts[2]
          : 0;

      return (
        sign *
        (
          hours * 60 +
          minutes +
          seconds / 60
        )
      );
    }
  }

  /* --------------------------------------------------------------------------
     FORMAT 2:
     +0m
     +10m
     +1h 20m
     -15m
     -------------------------------------------------------------------------- */

  const hoursMatch =
    cleanValue.match(
      /(\d+(?:\.\d+)?)\s*h/i,
    );

  const minutesMatch =
    cleanValue.match(
      /(\d+(?:\.\d+)?)\s*m/i,
    );

  const hours =
    hoursMatch
      ? Number(hoursMatch[1])
      : 0;

  const minutes =
    minutesMatch
      ? Number(minutesMatch[1])
      : 0;

  if (
    hoursMatch ||
    minutesMatch
  ) {
    return (
      sign *
      (
        (Number.isFinite(hours)
          ? hours
          : 0) *
          60 +
        (Number.isFinite(minutes)
          ? minutes
          : 0)
      )
    );
  }

  return 0;
};

/* ============================================================================
   MINUTES → VS TEAM DISPLAY
   ============================================================================ */

const formatVsTeam = (
  totalMinutes: number,
): string => {
  if (!Number.isFinite(totalMinutes)) {
    return '—';
  }

  const roundedMinutes =
    Math.round(totalMinutes);

  const sign =
    roundedMinutes >= 0
      ? '+'
      : '-';

  const absoluteMinutes =
    Math.abs(roundedMinutes);

  const hours =
    Math.floor(
      absoluteMinutes / 60,
    );

  const minutes =
    absoluteMinutes % 60;

  return `${sign}${hours
    .toString()
    .padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:00`;
};

/* ============================================================================
   COMPONENT
   ============================================================================ */

const WeeklyActiveHoursDetail = ({
  data,
}: Props) => {
  /* ==========================================================================
     SAFE DATA
     ========================================================================== */

  const rows =
    Array.isArray(data)
      ? data
      : [];

  /* ==========================================================================
     WTD AVG CALCULATIONS
     ========================================================================== */

  const rowCount =
    rows.length;

  const totalLoggedMinutes =
    rows.reduce(
      (total, row) =>
        total +
        timeToMinutes(
          row.logged,
        ),
      0,
    );

  const totalActiveMinutes =
    rows.reduce(
      (total, row) =>
        total +
        timeToMinutes(
          row.active,
        ),
      0,
    );

  const totalUtilisation =
    rows.reduce(
      (total, row) =>
        total +
        percentageToNumber(
          row.utilisation,
        ),
      0,
    );

  const totalVsTeamMinutes =
    rows.reduce(
      (total, row) =>
        total +
        vsTeamToMinutes(
          row.vsTeam,
        ),
      0,
    );

  const wtdLoggedAverage =
    rowCount > 0
      ? totalLoggedMinutes /
        rowCount
      : 0;

  const wtdActiveAverage =
    rowCount > 0
      ? totalActiveMinutes /
        rowCount
      : 0;

  const wtdUtilisationAverage =
    rowCount > 0
      ? totalUtilisation /
        rowCount
      : 0;

  const wtdVsTeamAverage =
    rowCount > 0
      ? totalVsTeamMinutes /
        rowCount
      : 0;

  /* ==========================================================================
     RENDER
     ========================================================================== */

  return (
    <div
      className="
        w-full
        overflow-hidden
        rounded-[14px]
        border
        border-[#D0D5DD]
        bg-white
        p-3
      "
    >
      {/* ======================================================================
          HEADER
          ====================================================================== */}

      <div
        className="
          mb-2
          flex
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[14px]
            font-semibold
            text-[#002B5B]
          "
        >
          Weekly Active Hours Detail
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-2.5
            py-[3px]
            text-[9px]
            font-medium
            text-[#2563EB]
          "
        >
          Day-by-day · utilisation · peer rank
        </span>
      </div>

      {/* ======================================================================
          TABLE
          ====================================================================== */}

      <div
        className="
          w-full
          overflow-x-auto
          bg-white
        "
      >
        <div
          className="
            w-full
            min-w-[600px]
            bg-white
          "
        >
          {/* ==================================================================
              TABLE HEADER
              ================================================================== */}

          <div
            className="
              grid
              w-full
              grid-cols-5
              border-b
              border-[#D0D5DD]
              bg-white
            "
          >
            {/* Day */}

            <div
              className="
                min-w-0
                bg-white
                py-[7px]
                text-left
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              Day
            </div>

            {/* Logged */}

            <div
              className="
                min-w-0
                bg-white
                py-[7px]
                text-left
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              Logged
            </div>

            {/* Active */}

            <div
              className="
                min-w-0
                bg-white
                py-[7px]
                text-left
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              Active
            </div>

            {/* Utilisation */}

            <div
              className="
                min-w-0
                bg-white
                py-[7px]
                text-left
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              Utilisation
            </div>

            {/* vs Team */}

            <div
              className="
                min-w-0
                bg-white
                py-[7px]
                text-left
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              vs Team
            </div>
          </div>

          {/* ==================================================================
              TABLE ROWS
              ================================================================== */}

          {rows.map((row, index) => (
            <div
              key={`${row.day}-${index}`}
              className="
                grid
                w-full
                grid-cols-5
                border-b
                border-[#EAECF0]
                bg-white
              "
            >
              {/* Day */}

              <div
                className="
                  min-w-0
                  truncate
                  bg-white
                  py-[5px]
                  text-[11px]
                  text-[#101828]
                "
              >
                {row.day}
              </div>

              {/* Logged */}

              <div
                className="
                  min-w-0
                  truncate
                  bg-white
                  py-[5px]
                  text-[11px]
                  text-[#101828]
                "
              >
                {formatTime(
                  row.logged,
                )}
              </div>

              {/* Active */}

              <div
                className="
                  min-w-0
                  truncate
                  bg-white
                  py-[5px]
                  text-[11px]
                  text-[#101828]
                "
              >
                {formatTime(
                  row.active,
                )}
              </div>

              {/* Utilisation */}

              <div
                className="
                  min-w-0
                  truncate
                  bg-white
                  py-[5px]
                  text-[11px]
                  text-[#101828]
                "
              >
                {row.utilisation ||
                  '—'}
              </div>

              {/* vs Team */}

              <div
                className="
                  min-w-0
                  truncate
                  bg-white
                  py-[5px]
                  text-[11px]
                  font-medium
                  text-[#16A34A]
                "
              >
                {row.vsTeam ||
                  '—'}
              </div>
            </div>
          ))}

          {/* ==================================================================
              WTD AVG
              ================================================================== */}

          <div
            className="
              grid
              w-full
              grid-cols-5
              border-t
              border-[#D0D5DD]
              bg-[#FFFFFF]
            "
          >
            {/* WTD Avg */}

            <div
              className="
                min-w-0
                bg-[#FFFFFF]
                pt-2
                pb-1
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              WTD Avg
            </div>

            {/* Logged Average */}

            <div
              className="
                min-w-0
                bg-[#FFFFFF]
                pt-2
                pb-1
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              {rowCount > 0
                ? minutesToTime(
                    wtdLoggedAverage,
                  )
                : '—'}
            </div>

            {/* Active Average */}

            <div
              className="
                min-w-0
                bg-[#FFFFFF]
                pt-2
                pb-1
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              {rowCount > 0
                ? minutesToTime(
                    wtdActiveAverage,
                  )
                : '—'}
            </div>

            {/* Utilisation Average */}

            <div
              className="
                min-w-0
                bg-[#FFFFFF]
                pt-2
                pb-1
                text-[11px]
                font-semibold
                text-[#101828]
              "
            >
              {rowCount > 0
                ? `${wtdUtilisationAverage.toFixed(0)}%`
                : '—'}
            </div>

            {/* VS Team Average */}

            <div
              className="
                min-w-0
                bg-[#FFFFFF]
                pt-2
                pb-1
                text-[11px]
                font-semibold
                text-[#16A34A]
              "
            >
              {rowCount > 0
                ? formatVsTeam(
                    wtdVsTeamAverage,
                  )
                : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyActiveHoursDetail;